import { Easing, GameRandom, Inject, List, ModelEvents, Singleton } from "../../modules/@denishussar/html5-game-engine";
import { GameModalsEnum, GameModel } from "../Models/GameModel";
import { GameStorageManager } from "../../modules/@denishussar/html5-game-engine/dist";
import { PlayerJumpingStateConstants } from "../Constants/PlayerJumpingStateConstants";
import { AABB } from "../Utils/AABB";
import { AbstractBgTileContainerModel } from "../Models/BgContainerModels/AbstractBgTileContainerModel";
import { AbstractBgTileModel } from "../Models/BgContainerModels/TileModels/AbstractBgTileModel";
import { EnumUtils } from "../Utils/Helpers";
import { StopperNamesConstants } from "../Constants/StopperNamesConstants";
import { OutfitNamesConstants } from "../Constants/OutfitNamesConstants";
import { OutfitCollisionModel } from "../Models/BgContainerModels/CollisionSubjectModel/OutfitCollisionModel";
import { StopperCollisionModel } from "../Models/BgContainerModels/CollisionSubjectModel/StopperCollisionModel";
import { CollisionSubjectModel } from "../Models/BgContainerModels/CollisionSubjectModel/CollisionSubjectModel";
import CommonRuleConstants from "../Constants/CommonRuleConstants";
import { BgCollisionContainerModel } from "../Models/BgCollisionContainerModel";
import { ModelEventConstants } from "../Constants/ModelEventConstants";
import { PromocodeTypeConstants } from "../Constants/PromocodeTypeConstants";
import { PrizeThresholdConstants } from "../Constants/PrizeThresholdConstants";
import { Debug } from "../Utils/Debug";
import { FirebaseAPI } from "../../FirebaseAPI";
import { AnalyticsConstants } from "../Constants/AnalyticsConstants";

const INTERACTION_TIMEOUT = 15 * 60 * 1000;

@Singleton
export class GameLogic {
    private _stopperNames = EnumUtils.values(StopperNamesConstants);
    private _outfitNames = EnumUtils.values(OutfitNamesConstants);
    private _dtOutfit = 0;
    private _dtStopper = 0;
    private _dtBonus = 0;

    constructor(@Inject private _model: GameModel,
        @Inject private _random: GameRandom,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _storageManager: GameStorageManager,
        @Inject private _firebaseAPI: FirebaseAPI) {
        console.log('GameLogic', this);

        this.load();
    }


    public load() {
        this._storageManager.loadModel("-game", this._model, true, true, (data) => {
            return data;
        });
    }

    public save() {
        this._storageManager.saveModel("-game", this._model, {
            hiScore: true,
            lastScore: true,
            tutorialWasShow: true,
            player: {
                guid: true
            }
        }, true, true, (data) => {
            return data;
        });
    }

    public startNewGame() {
        this._clearAll();
        const { predictionDistance } = this._model.bgCollisionContainer
        this._clearNextMeters(3000);
        this._model.startNewGame = true;
        this._model.tapToStart = true;
        this._model.player.isRun = false;
        this._model.player.isFall = false;
        this._model.pause = false;
        this._model.player.lives = 3;
        this._model.outfitAmount = 0;
        this._model.distance = 0;
        this._model.userProfile.coinsCount = 0;
        this._model.collectAchievements = [0, 0, 0, 0, 0, 0, 0, 0];
        this._model.levelSpeed = 1.5;
        this._model.player.pos.x = this._model.player.initPos.x;
        this._model.distanceToGenerateOutfit = CommonRuleConstants.DistanceToGenerateOutfit;
        this._model.distanceToGenerateBonus = CommonRuleConstants.DistanceToGenerateBonus;
        this._model.distanceToGenerateStopper = CommonRuleConstants.DistanceToGenerateStopper;
        this._model.bonusCount = 0;
        this._model.actualPrize = null;
    }

    public exitGame() {
        this._model.startNewGame = false;
        this._model.exitGame = true;
        this._clearAll();
    }

    public gameTap() {
        this._model.tapToStart = false;
        if (this._model.player.lives <= 0) {
            return;
        }

        if (!this._model.player.isRun) {
            if (this._model.player.lives < CommonRuleConstants.MaxLives) {
                this._model.player.pos.x = this._model.player.initPos.x;
                this._clearNextMeters(3000);
            }

            this.setIsRun(true);
        } else {
            this.jump();
        }
    }

    public setIsRun(isRun: boolean) {
        if (isRun) {
            this._model.player.isFall = false;
        }
        this._model.player.isRun = isRun;
    }

    public jump() {
        let isSecondJumpAvailable = false;
        const { jumpingState } = this._model.player;
        switch (jumpingState) {
            case PlayerJumpingStateConstants.None:

                isSecondJumpAvailable = true;
                this._model.player.jumpingState = PlayerJumpingStateConstants.One;
                this._model.player.jumpAccelerationY = -7000; // use for control speed jump up
                this._model.player.jumpSpeedY = 3500; // use for control speed jump up
                this._model.player.jumpStartAt = Date.now();
                this._modelEvents.dispatch(this._model, ModelEventConstants.PLAYER_JUMP);
                break;
        }
    }

    private getPromoCodeTypeList(): List<PromocodeTypeConstants> {
        return new List<PromocodeTypeConstants>(this._model.promocodeList.map(item => item.type));
    }

    private isCatchPrize(type: PromocodeTypeConstants): boolean {
        return this.getPromoCodeTypeList().contains(type);
    }

    private checkPrize(): void {
        // catch one per session
        if (this._model.actualPrize) {
            return;
        }

        // catch all
        if (this.isCatchPrize(PromocodeTypeConstants.type1) &&
            this.isCatchPrize(PromocodeTypeConstants.type2) &&
            this.isCatchPrize(PromocodeTypeConstants.type3)) {
            return
        }

        const coins = this.getWinCoins();

        let prize = null;

        // Detect prize type
        if (coins >= PrizeThresholdConstants.type3 && !this.isCatchPrize(PromocodeTypeConstants.type3)) {
            prize = PromocodeTypeConstants.type3;

        } else if (coins >= PrizeThresholdConstants.type2 && !this.isCatchPrize(PromocodeTypeConstants.type2)) {
            prize = PromocodeTypeConstants.type2;

        } else if (coins >= PrizeThresholdConstants.type1 && !this.isCatchPrize(PromocodeTypeConstants.type1)) {
            prize = PromocodeTypeConstants.type1;
        }

        if (prize) {
            this._model.actualPrize = prize;
            this._modelEvents.dispatch(this._model, 'showPrizeEffect', prize);
        }
    }

    public update(frameRate: number) {
        if (this._model.player.isRun) {
            this._model.distance += this._model.player.speed * this._model.levelSpeed * frameRate;

            // check prize
            this.checkPrize();

            // bg
            this._updateBgTileContainer(this._model.bgContainer, frameRate);
            this._updateBgTileContainer(this._model.fenceContainer, frameRate);
            this._updateBgTileContainer(this._model.mainCityContainer, frameRate);
            this._updateBgTileContainer(this._model.backCityContainer, frameRate);

            // bg collisions
            this._updateBgCollisionContainer(this._model.bgCollisionContainer, frameRate);


            // collision
            const collisionSubj = this._chekPlayerCollision();
            if (collisionSubj) {
                if (collisionSubj instanceof OutfitCollisionModel) {
                    if (!collisionSubj.isCollected) {
                        this._model.outfitAmount++;
                        collisionSubj.isCollected = true;
                        this._modelEvents.dispatch(this._model, ModelEventConstants.COLLISION_OUTFIT, collisionSubj.name);
                    }
                } else {
                    this._model.player.lives--;
                    this._model.player.isRun = false;
                    this._model.player.isFall = true;
                    this._model.player.fallAt = Date.now();
                    this._model.player.jumpingState = PlayerJumpingStateConstants.None;
                    this._modelEvents.dispatch(this._model, ModelEventConstants.COLLISION_STOPPER, collisionSubj.name);
                    if (!this._model.player.lives) {
                        this.gameOver();
                    }
                }
            }
            this._model.levelSpeed += 0.006 * frameRate;
        }

        //player
        this._updatePlayer(frameRate);

    }

    private _chekPlayerCollision(): CollisionSubjectModel {
        const playerAabbList = this._model.player.aabbComposite;

        for (let index = 0; index < this._model.bgCollisionContainer.collisionSubject.length; index++) {
            const subj = this._model.bgCollisionContainer.collisionSubject[index];

            for (let playerIndex = 0; playerIndex < playerAabbList.length; playerIndex++) {
                const playerAabb = playerAabbList[playerIndex];
                const res = this._checkCollision(subj.aabb, playerAabb);
                if (res) {
                    return subj
                }
            }
        }
        ;

        return null;
    }

    private _checkCollision(a: AABB, b: AABB) {
        if (a.max.x < b.min.x || a.min.x > b.max.x) return false;
        if (a.max.y < b.min.y || a.min.y > b.max.y) return false;
        return true;
    }

    private _generateOutFit(isBonus = false) {

        let pool;
        let type;

        type = 'outfit';
        pool = this._model.bgCollisionContainer.outfitPool;

        let { isNew, instance } = pool.get();

        instance.pos.x = 4000;
        this._model.bgCollisionContainer.collisionSubject.push(instance);
        instance.elapsed = 0;
        instance.startPos.x = 4000;
        instance.isActive = true;
        instance.isCollected = false;
        instance.name = this._outfitNames[this._random.integer(0, this._outfitNames.length)];
        (instance as OutfitCollisionModel).outfitName = isBonus ? 7 : this._random.integer(0, 7);

        this._modelEvents.dispatch(this._model.bgCollisionContainer, ModelEventConstants.ADD_COLLISION_SUBJECT, instance);
    }

    private _generateStopper() {
        let pool;
        let type;

        type = 'stopper';
        pool = this._model.bgCollisionContainer.stopperPool;

        let { isNew, instance } = pool.get();

        instance.pos.x = 5000;
        this._model.bgCollisionContainer.collisionSubject.push(instance);
        instance.elapsed = 0;
        instance.startPos.x = 5000;
        instance.isActive = true;
        instance.isCollected = false;
        instance.name = this._stopperNames[this._random.integer(0, this._stopperNames.length)];
        this._modelEvents.dispatch(this._model.bgCollisionContainer, ModelEventConstants.ADD_COLLISION_SUBJECT, instance);
    }

    private _updateBgCollisionContainer(container: BgCollisionContainerModel, frameRate: number) {
        const { collisionSubject, predictionDistance } = this._model.bgCollisionContainer;
        const { levelSpeed } = this._model;

        for (let index = 0; index < collisionSubject.length; index++) {
            collisionSubject[index].pos.x -= frameRate * levelSpeed * this._model.bgCollisionContainer.speed;
            if (collisionSubject[index].pos.x < -collisionSubject[index].width) {

                collisionSubject[index].isActive = false;
            }

        }
        this._dtOutfit += Math.random() * 2 + 1;
        this._dtStopper += Math.random() * 2 + 1;
        this._dtBonus += Math.random() * 2 + 1;
        if (this._dtOutfit >= 100) {
            this._dtOutfit = 0;
            if (this._dtBonus > 500) {
                this._dtBonus = 0;
                if (this._model.bonusCount < 3) {
                    this._generateOutFit(true);
                } else {
                    this._generateOutFit();
                }
            } else {
                this._generateOutFit();
            }

            return;
        }


        if (this._dtStopper >= 300) {
            this._dtStopper = 0;

            this._generateStopper();
            return;
        }
    }

    private _updateBgTileContainer(container: AbstractBgTileContainerModel<AbstractBgTileModel>, frameRate: number) {
        const { levelSpeed } = this._model;
        const tiles = container.tiles;
        tiles[0].pos.x -= levelSpeed * container.speed * frameRate;
        for (let index = 1; index < tiles.length; index++) {
            tiles[index].pos.x = tiles[0].pos.x + index * tiles[0].width;

        }
        if (tiles[0].pos.x <= -tiles[0].width) {
            tiles[0].pos.x = (tiles.length - 1) * tiles[0].width;
            tiles.push(tiles.shift());
        }
    }

    private _updatePlayer(frameRate: number) {
        const player = this._model.player;
        if (player.jumpingState === PlayerJumpingStateConstants.ForceDown) {
            player.pos.y += 4000 * frameRate;
        } else {
            player.pos.y = player.pos.y - player.jumpSpeedY * frameRate;
        }

        if (player.pos.y >= 0) {
            player.pos.y = 0;
            player.jumpSpeedY = 0;
            player.jumpAccelerationY = 0;
            player.jumpingState = PlayerJumpingStateConstants.None;
        } else {
            const newSpeed = player.jumpSpeedY + player.jumpAccelerationY * frameRate * 2;
            if (newSpeed < 0 && player.jumpSpeedY > 0) {
                player.jumpAccelerationY = -1400; // use for control speed jump down
            }
            player.jumpSpeedY = newSpeed;
        }

        if (player.isFall) {
            const distance = 400;
            const progress = Easing.Quadratic.Out(Math.min(1, (Date.now() - player.fallAt) / 400));
            player.pos.x = player.initPos.x + progress * distance;
            player.jumpSpeedY = Math.min(-1000, player.jumpSpeedY);
            player.jumpAccelerationY = Math.min(-5000, player.jumpAccelerationY);

        }
    }

    private _clearNextMeters(px: number) {
        for (let index = 0; index < this._model.bgCollisionContainer.collisionSubject.length; index++) {
            const subj = this._model.bgCollisionContainer.collisionSubject[index];
            if (subj.pos.x < px) {
                subj.isActive = false;
                this._model.bgCollisionContainer.collisionSubject.shift();
                index--;
            }
        }
    }

    private _clearAll() {
        let index = 0;
        while (this._model.bgCollisionContainer.collisionSubject.length) {
            const subj = this._model.bgCollisionContainer.collisionSubject.pop();
            subj.isActive = false;
        }
    }

    public showMainMenuContainer() {
        this._model.showMainMenuContainer = true;
    }

    public hideMainMenuContainer() {
        this._model.showMainMenuContainer = false;
    }

    public showMainMenu() {
        this._model.showMainMenu = true;
    }

    public showTutorial() {
        this._model.modalState = GameModalsEnum.tutorial;
    }

    public showTutor() {
        if (!this._model.tutorialWasShow) {
            this._model.modalState = GameModalsEnum.tutor;
            this._model.showPopupShading = !this._model.pause;
            this._model.tutorialWasShow = true;
            this.save();

            return true;
        }

        return false;
    }

    public showEnterName() {
        this._model.modalState = GameModalsEnum.enterName;
    }

    public showChoosePerson() {
        this._model.modalState = GameModalsEnum.choosePerson;
    }

    public showTapToStart() {
        this._model.modalState = GameModalsEnum.tapToStart;
    }

    public hideMainMenu() {
        this._model.showMainMenu = false;
    }

    public settings() {
        this._model.modalState = GameModalsEnum.settings;
    }

    public async prizes() {
        /*this.showLoader();
        this.hideLoader();

        this._model.modalState = GameModalsEnum.prizes;*/
        //this._model.modalState = GameModalsEnum.my_prize;
    }

    public myPrize() {
        //this._model.modalState = GameModalsEnum.my_prize;
    }

    public async prizesShadow() {
        this.showLoader();
        this.hideLoader();

        this._model.modalState = GameModalsEnum.prizes;
        this._model.showPopupShading = true;
        this._model.prizes = !this._model.prizes;
        this._model.pause = !this._model.pause;

        if (this._model.prizes) {
            this._model.modalState = GameModalsEnum.prizes;
        }
    }

    public promocodes() {
        this._model.modalState = GameModalsEnum.promocodes;
    }

    public copiedStatus() {
        this._model.copiedStatus = true;
        setTimeout(() => {
            this._model.copiedStatus = false;
        }, 1500)
    }


    public promocodesList() {
        this._model.modalState = GameModalsEnum.promocodes_list;
    }

    public exitMenu() {
        this._model.modalState = GameModalsEnum.exitMenu;
    }

    showError(text: string) {
        this.showMainMenuContainer();
        this._model.showPopupShading = true;
        this._model.modalState = GameModalsEnum.error;
    }

    public pause() {
        this._model.pause = true;
    }

    public unpause() {
        this._model.pause = false;
    }

    showPause() {
        this._model.modalState = GameModalsEnum.pause;
        this._model.showPopupShading = !this._model.pause;
        if (this._model.pause) {
            this._model.modalState = GameModalsEnum.pause;
        }
    }

    public returnPause() {
        this._model.modalState = GameModalsEnum.pause;
    }

    public async leaderBoardShadow() {
        this.showLoader();

        this._model.modalState = GameModalsEnum.leader_board;
        this._model.showPopupShading = !this._model.leaderBoard;
        this._model.leaderBoard = !this._model.leaderBoard;

        if (this._model.leaderBoard) {
            this._model.modalState = GameModalsEnum.leader_board;
        }
    }

    public async leaderBoard() {
        this.showLoader();
        this._model.modalState = GameModalsEnum.leader_board;
    }

    public outfit() {
        this._model.modalState = GameModalsEnum.outfit;
    }

    public hideModal() {
        this._model.modalState = GameModalsEnum.none;
        this._model.showPopupShading = false;
    }

    public showLoader() {
        this._model.showPopupShading = false;
        this._model.modalState = GameModalsEnum.loader;
    }

    public hideLoader() {
        this._model.showPopupShading = false;
        this._model.modalState = GameModalsEnum.none;
    }

    public changeOutfit(revert: boolean) {
        if (revert) {
            if (this._model.outfitView > 3) {
                this._model.outfitView = 0;
            } else {
                this._model.outfitView++;
            }

        } else {
            if (this._model.outfitView === 0) {
                this._model.outfitView = 4;
            } else {
                this._model.outfitView--;
            }

        }

    }

    public getWinCoins() {
        return Math.round((this._model.distance + this._model.outfitAmount * 50));
    }

    public setMusicOn(isOn: boolean) {
        this._model.isMusicOn = isOn;
    }

    public setSoundsOn(isOn: boolean) {
        this._model.isSoundsOn = isOn;
    }

    public async gameOver() {

        this._model.userProfile.coinsCount = parseInt(this._model.userProfile.coinsCount as string) + this.getWinCoins();
        this._model.player.score = /*parseInt(this._model.userProfile.coinsCount as string) + */this.getWinCoins();
        this._model.startNewGame = false;
        console.log('game over');
        await this._firebaseAPI.analytics(this._model.playerGender, AnalyticsConstants.LOSING);

        await this.saveDB();
        const base = await this._firebaseAPI.getUsers();
        console.log(base);

        this._model.leaderboard = [];
        for (let i = 0; i < base.length; i++) {
            this._model.leaderboard.push(
                {
                    id: base[i].id,
                    name: base[i].data.name,
                    score: base[i].data.score
                }
            )
        }
        this.leaderBoardShadow();
    }

    public showGameEnd() {
        this._model.modalState = GameModalsEnum.gameover;
    }

    public addBonus() {
        this._model.bonusCount++;
    }

    public addOutFitCollect(outfitId: number) {
        this._model.collectAchievements[outfitId]++;
    }

    public getBonusCount(): number {
        return this._model.bonusCount;
    }

    public async sendAnalitics(eventName: AnalyticsConstants) {
        this._firebaseAPI.analytics(this._model.playerGender, eventName);
    }

    // Firebase
    public async initStorage(): Promise<void> {
        const currentId = this._storageManager.loadData('currentId_t01');

        if (currentId !== null && typeof currentId !== 'undefined') {
            this._model.player.id = currentId;
        } else {
            this._model.player.id = '';
        }
    }

    public async initDB(): Promise<void> {
        this._firebaseAPI.init();
    }

    public async loadDB(): Promise<void> {
        const id = this._model.player.id;

        const data = await this._firebaseAPI.load(id.toString());

        if (data) {
            console.log(
                `found userdata ${this._model.player.id} ${JSON.stringify(
                    data
                )}`
            );
            this._model.player.name = data.name;
            this._model.player.totalScore = data.score;
            console.log('player total score ', this._model.player.totalScore)

        } else {
            console.log(`userdata not found`);
            this._model.player.name = '';
        }
    }

    public async saveDB(): Promise<void> {
        const id = this._model.player.id;
        const score = this.getWinCoins();
        const totalScore = this._model.player.totalScore;
        //const achievements = this._model.collectAchievements;
        const name = this._model.player.name;

        if ((score === 0 && totalScore === 0) || score > totalScore) {
            console.log('SAVE SCORE!!!!', id, name, score, totalScore);

            const currentId = await this._firebaseAPI.save(id, {
                name: name,
                score: score,
                //achievements: achievements
            });

            this._model.player.name = name;
            this._model.player.id = currentId;
            if (score > totalScore) {
                this._model.player.totalScore = score;
            }

            this._storageManager.saveData('currentId_t01', currentId);
        }

        // console.log('save DB', 'name', name, 'score', score, 'prevscore', totalScore);
    }
}
